import React, { useEffect, useCallback, useState } from 'react';
import { BizonCheckoutWrapper, BizonScreenWrapper } from 'components/commons';
import { Route } from 'react-router';
import DocumentCheckout from './document-checkout';
import AllowedDocumentsUploader from './allowed-documents-uploader';
import AllowedDocumentsSignerUploader from './allowed-documents-uploader-signers';
import { useNavigation } from 'hooks/index';
import {
    useCompanyInfoCheckListQuery,
    Signer,
    Checklist
} from 'generated/credit-analysis-graphql';
import Loading from 'components/loading';
import { ApolloClientName } from 'apollo-client-factory';
import './assets/css/documents-screen.styles.scss';
import {
    checklistLabel,
    getPartnerWithPendingDocuments,
    getPendingType
} from './document-functions';
import ChooseBankAccount from './choose-bank-account';
import {
    LoanApplication,
    LoanApplicationStatus,
    useAntiFraudQuery,
    useGetLoanQuery,
    WorthyCreditStatus
} from 'generated/graphql';
import { getLoanApplicationParams } from 'utils/utils';
import WorthyCreditQuestionnaire from 'containers/antifraud/worthy-credit';
import { AntifraudSteps, useCompleteAntifraudStep } from 'hooks/complete-antifraud-step';

const documentsPath = "/:loanApplicationId/documentacao";

const DocumentContainer: React.FC = (): JSX.Element => {
    const { goBack, goToSendCompanyDocuments, goToSendPartnerDocuments, goToChooseBankAccount } = useNavigation();
    const loanApplicationId = getLoanApplicationParams();

    const { data, refetch: refetchLoan } = useGetLoanQuery({
        variables: {
            id: loanApplicationId
        },
        fetchPolicy:"network-only"
    });

    const { data: companyInfoData, loading, refetch } = useCompanyInfoCheckListQuery({
        variables: { loanApplicationId },
        context: { clientName: ApolloClientName.CreditAnalysisApi },
        fetchPolicy: "no-cache"
    });
    const [questionnaireIsCompleted, setQuestionnaireIsCompleted] = useState(false);

    const mustChooseBankAccount = useCallback(
        (lApplication: LoanApplication) =>
            lApplication &&
            lApplication.status === LoanApplicationStatus.InAnalysis &&
            lApplication.acceptedBizOsAccount === null,
        []
    );

    const {
        data: antiFraudData,
        loading: antifraudLoading,
    } = useAntiFraudQuery({ variables: { loanApplicationId } });

    const antifraudSteps: AntifraudSteps = []
    const { handleCompleteAntiFraudStep } = useCompleteAntifraudStep({ antifraudSteps });

    useEffect(() => {
        if (mustChooseBankAccount(data?.loanApplication)) {
            return goToChooseBankAccount();
        }
    }, [data?.loanApplication]);

    useEffect(() => {
        if (antiFraudData?.antiFraud
            && (antiFraudData.antiFraud.worthyCreditStatus === WorthyCreditStatus.Generated
                || antiFraudData.antiFraud.worthyCreditStatus === WorthyCreditStatus.Draft)) {
            setQuestionnaireIsCompleted(true);
        }
    }, [antiFraudData]);

    if (loading || antifraudLoading) {
        return (
            <Loading />
        )
    }

    const { checklist, signers } = companyInfoData?.companyInfo;

    const goToNextScreen = (clt: Checklist, sgn: Signer[]) => {
        const [pendingType] = getPendingType(clt.items)
        if (pendingType) {
            return goToSendCompanyDocuments(
                pendingType,
                checklistLabel[pendingType[0]].type
            );
        }

        const partnersWithPendingDocuments = getPartnerWithPendingDocuments(sgn)
        if (partnersWithPendingDocuments) {
            const [signerPendingType] = getPendingType(partnersWithPendingDocuments.checklist.items)
            if (signerPendingType) {
                return goToSendPartnerDocuments(signerPendingType, checklistLabel[signerPendingType[0]].type, partnersWithPendingDocuments);
            }
        }
    }

    return (
        <>
            <Route
                exact
                path={documentsPath}
                render={() => {
                    return (antiFraudData?.antiFraud
                        && !questionnaireIsCompleted
                        && (antiFraudData?.antiFraud.worthyCreditStatus === WorthyCreditStatus.Generated
                            || antiFraudData?.antiFraud.worthyCreditStatus === WorthyCreditStatus.Draft))
                        ? <BizonScreenWrapper>
                            <WorthyCreditQuestionnaire
                                onComplete={() => {
                                    handleCompleteAntiFraudStep()
                                    setQuestionnaireIsCompleted(true)
                                }}
                            />
                          </BizonScreenWrapper>
                        : <BizonCheckoutWrapper>
                            <DocumentCheckout
                                companyInfo={companyInfoData?.companyInfo}
                                nextScreen={() => goToNextScreen(checklist, signers)}
                            />
                        </BizonCheckoutWrapper>
                }}
            />
            <Route
                path={`${documentsPath}/envio-documentos/`}
                render={() => (
                    <BizonScreenWrapper>
                        <AllowedDocumentsUploader
                            onBackClick={goBack}
                            nextScreen={async () => {
                                await refetch();
                                const { data } = await refetchLoan();
                                if (mustChooseBankAccount(data.loanApplication)) {
                                    goToChooseBankAccount();
                                    return;
                                }
                                goBack();
                            }}
                        />
                    </BizonScreenWrapper>
                )}
            />
            <Route
                path={`${documentsPath}/envio-documentos-socio/:signerId/`}
                render={() => (
                    <BizonScreenWrapper>
                        <AllowedDocumentsSignerUploader
                            onBackClick={goBack}
                            nextScreen={async () => {
                                await refetch();
                                const { data } = await refetchLoan();
                                if (mustChooseBankAccount(data.loanApplication)) {
                                    goToChooseBankAccount();
                                    return;
                                }
                                goBack();
                            }}
                        />
                    </BizonScreenWrapper>
                )}
            />
            <Route
                path={`${documentsPath}/escolha-conta-bancaria/`}
                render={() => (
                    <BizonScreenWrapper>
                        <ChooseBankAccount nextScreen={goBack} />
                    </BizonScreenWrapper>
                )}
            />
        </>
    );
};

export default DocumentContainer;
